import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X, Cookie, Shield } from 'lucide-react';
import { getConsentStatus, setConsentStatus, initializeGA4 } from '@/utils/analytics';

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Initialize GA4 on mount
    initializeGA4();
    
    // Check if user has already made a choice
    const consent = getConsentStatus();
    if (consent === 'pending') {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    setConsentStatus('granted');
    setIsVisible(false);
  };

  const handleDecline = () => {
    setConsentStatus('denied');
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-card border border-border rounded-2xl shadow-xl overflow-hidden">
            {/* Main Banner */}
            <div className="p-4 md:p-6">
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="hidden sm:flex w-12 h-12 rounded-xl bg-primary/10 items-center justify-center shrink-0">
                  <Cookie className="w-6 h-6 text-primary" />
                </div>
                
                {/* Content */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-foreground">
                      Your Privacy Matters
                    </h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="sm:hidden h-8 w-8"
                      onClick={handleDecline}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <p className="text-sm text-muted-foreground">
                    SoulSafe uses analytics to improve your experience. We{' '}
                    <span className="font-medium text-foreground">never</span> track 
                    your journal entries, prayers, reflections, or any personal content.
                  </p>

                  {/* Expandable Details */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-3 space-y-3 border-t border-border/50">
                          <div className="flex items-start gap-3">
                            <Shield className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-foreground">What we track:</p>
                              <p className="text-xs text-muted-foreground">
                                Page visits, module progress, feature usage (anonymized)
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <Shield className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-foreground">What we never track:</p>
                              <p className="text-xs text-muted-foreground">
                                Journal entries, prayers, testimonies, chat messages, personal notes, 
                                names, emails, or any identifying information
                              </p>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground italic">
                            Analytics helps us understand how to better serve survivors. 
                            Your healing journey is sacred and private.
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-xs text-primary hover:underline"
                  >
                    {isExpanded ? 'Show less' : 'Learn more about our privacy commitment'}
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2 p-4 pt-0 md:p-6 md:pt-0">
              <Button
                variant="outline"
                className="flex-1 sm:flex-none"
                onClick={handleDecline}
              >
                Decline
              </Button>
              <Button
                className="flex-1 sm:flex-none"
                onClick={handleAccept}
              >
                Accept Analytics
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
